import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_TESTIMONIALS = [
  {
    id: "1",
    name: "Michael Rodriguez, PE",
    title: "Senior Structural Engineer",
    company: "Atlantic Engineering Group",
    location: "Miami, FL",
    quote: "After specifying GEObar B on three coastal projects, I can confidently say it's the future of marine construction. The corrosion immunity alone justifies the investment.",
    rating: 5,
    project: "Miami Beach Seawall",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80"
  },
  {
    id: "2",
    name: "Sarah Chen",
    title: "Director of Construction",
    company: "Pacific Northwest Builders",
    location: "Seattle, WA",
    quote: "The weight savings with BFRP rebar reduced our crane costs by 40% on the waterfront project. Installation crews love how easy it is to handle.",
    rating: 5,
    project: "Seattle Harbor Terminal",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=150&q=80"
  },
  {
    id: "3",
    name: "James Thompson",
    title: "VP of Engineering",
    company: "Midwest Infrastructure Partners",
    location: "Chicago, IL",
    quote: "We've eliminated our corrosion maintenance budget on parking structures using Standard Composites products. The lifecycle cost analysis made this an easy decision.",
    rating: 5,
    project: "Chicago Parking Authority",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&q=80"
  },
  {
    id: "4",
    name: "Dr. Amanda Foster",
    title: "Hospital Facilities Director",
    company: "Regional Medical Center",
    location: "Houston, TX",
    quote: "The non-magnetic properties of GEObar FG were essential for our MRI wing expansion. Zero interference with our imaging equipment, exactly as promised.",
    rating: 5,
    project: "MRI Suite Expansion",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&q=80"
  },
  {
    id: "5",
    name: "Robert Martinez",
    title: "Project Manager",
    company: "Gulf Coast Construction",
    location: "Tampa, FL",
    quote: "Standard Composites delivered on time and their technical support was exceptional. They helped us design the most efficient reinforcement layout for our bridge deck.",
    rating: 5,
    project: "Tampa Bay Bridge Repair",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=150&q=80"
  },
  {
    id: "6",
    name: "Emily Watson, PE",
    title: "Principal Engineer",
    company: "Coastal Design Associates",
    location: "San Diego, CA",
    quote: "We've specified BFRP on every coastal project since 2023. The 100+ year service life means our clients never worry about reinforcement failure.",
    rating: 5,
    project: "La Jolla Seawall",
    image: "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?auto=format&fit=crop&w=150&q=80"
  }
];

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/testimonials`);
        if (response.data.testimonials?.length > 0) {
          setTestimonials(response.data.testimonials);
        }
      } catch (error) {
        console.log('Using fallback testimonials');
      }
    };
    fetchTestimonials();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  return (
    <section className="py-24 lg:py-32 bg-surface-dark/30 border-y border-border-dark" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold/50" />
            <span className="tech-label">//CLIENT_FEEDBACK</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold/50" />
          </div>
          <h2 className="heading-display text-4xl md:text-5xl text-stone">
            Trusted by <span className="text-gold">Engineers</span>
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative">
          <div className="overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
              >
                <div className="bg-charcoal border border-border-dark p-8 md:p-12 relative">
                  {/* Quote icon */}
                  <Quote size={48} className="absolute top-6 right-6 text-gold/10" />
                  
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    {/* Avatar */}
                    <div className="shrink-0">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-gold/30">
                        <img 
                          src={currentTestimonial.image} 
                          alt={currentTestimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(currentTestimonial.rating)].map((_, i) => (
                          <Star key={i} size={16} className="fill-gold text-gold" />
                        ))}
                      </div>

                      {/* Quote */}
                      <blockquote className="text-stone text-lg md:text-xl leading-relaxed mb-6">
                        "{currentTestimonial.quote}"
                      </blockquote>

                      {/* Author */}
                      <div className="border-t border-border-dark pt-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div>
                            <p className="text-gold font-semibold">{currentTestimonial.name}</p>
                            <p className="text-stone/50 text-sm">
                              {currentTestimonial.title}, {currentTestimonial.company}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-mono text-xs text-stone/40">{currentTestimonial.location}</p>
                            <p className="text-mono text-xs text-gold/60">Project: {currentTestimonial.project}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > currentIndex ? 1 : -1);
                    setCurrentIndex(i);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex ? 'bg-gold w-6' : 'bg-stone/30 hover:bg-stone/50'
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>

            {/* Arrows */}
            <div className="flex gap-2">
              <button
                onClick={goToPrevious}
                className="p-3 border border-border-dark hover:border-gold/50 transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} className="text-stone" />
              </button>
              <button
                onClick={goToNext}
                className="p-3 border border-border-dark hover:border-gold/50 transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} className="text-stone" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
